// base
import { createSlice } from '@reduxjs/toolkit';
import { USER_SLICE_NAME } from '../actions/user.action';

// thunks
import {
  getUsers,
  getUserDetail,
  createUser,
  updateUser,
  deleteUser,
  deleteUserData,
  getUserFileUrl
} from '../thunks/user.thunk';
import { TotalMapProps } from 'modules/common';
import { IUserDetail, IUser, IWorkerSafetyHistory, ReqFetchUsers } from '../models/user.model';
import { getWorkerSafetyHistories, ReqWorkerSafetyHistories } from 'modules/worker';
import { DEFAULT_PAGE, DEFAULT_PAGE_SIZE } from 'constants/const';
import { IDialog, IDialogType, IPageAutority } from 'modules/notice/models/notice.model';

export interface UserState {
  users: IUser[];
  usersTotalMap: TotalMapProps;

  userDetail: IUserDetail;
  safetyHistories: IWorkerSafetyHistory[];
  safetyHistoriesTotalMap: TotalMapProps;

  fetchUsersRequest: ReqFetchUsers;
  fetchUserSafetyHistoriesReqeust: ReqWorkerSafetyHistories;

  dialog: IDialog;
  pageAuthority: IPageAutority;
}

// initialState
const initialState: UserState = {
  userDetail: {
    email: '',
    birthDate: '',
    bloodType: '',
    userId: '',
    workerId: '',
    usernameV2: {
      firstName: '',
      middleName: '',
      lastName: '',
      fullName: '',
      title: ''
    },
    companyId: '',
    companyName: '',
    designationId: '',
    designationName: '',
    mobile: '',
    nationId: '',
    nationName: '',
    iqamaNo: '',
    mobilization: '',
    passportAssign: '',
    safetyHistoryList: [],
    status: '',
    role: '',
    userName: '',
    userFileId: '',
    file: undefined,
    fileUrl: undefined
  },
  users: [],
  usersTotalMap: {
    totalElements: 0,
    totalPages: 0
  },

  safetyHistories: [],
  safetyHistoriesTotalMap: {
    totalElements: 0,
    totalPages: 0
  },
  fetchUsersRequest: {
    company: 'ALL',
    passport: 'ALL',
    keyword: '',
    page: DEFAULT_PAGE,
    size: DEFAULT_PAGE_SIZE
  },
  fetchUserSafetyHistoriesReqeust: {
    categoryId: 'ALL',
    trainingId: '',
    page: DEFAULT_PAGE,
    size: DEFAULT_PAGE_SIZE
  },
  dialog: {
    message: undefined,
    type: IDialogType.none,
    isNavigate: false,
    callback: () => {}
  },
  pageAuthority: {
    isReadonly: false,
    hasAuthority: false
  }
};

// createSlice
const userSlice = createSlice({
  name: USER_SLICE_NAME,
  initialState,
  reducers: {
    changeFetchUsersRequest: (state, action) => {
      state.fetchUsersRequest = {
        ...state.fetchUsersRequest,
        ...action.payload
      };
    },
    resetFetchUserRequest: (state) => {
      state.fetchUsersRequest = initialState.fetchUsersRequest;
    },

    changeUserDetail: (state, action) => {
      state.userDetail = {
        ...state.userDetail,
        ...action.payload
      };
    },
    resetUserDetail: (state) => {
      state.userDetail = initialState.userDetail;
    },

    changeFetchUserSafetyHistoriesRequest: (state, action) => {
      state.fetchUserSafetyHistoriesReqeust = {
        ...state.fetchUserSafetyHistoriesReqeust,
        ...action.payload
      };
    },
    resetFetchUserSafetyHistoriesRequest: (state) => {
      state.fetchUserSafetyHistoriesReqeust = initialState.fetchUserSafetyHistoriesReqeust;
    },

    changeDialog: (state, action) => {
      state.dialog = {
        ...state.dialog,
        ...action.payload
      };
    },
    resetDialog: (state) => {
      state.dialog = initialState.dialog;
    },

    changePageAuthority: (state, action) => {
      state.pageAuthority = {
        ...state.pageAuthority,
        ...action.payload
      };
    },
    resetAll: () => initialState
  },
  extraReducers: (builder) => {
    builder
      .addCase(getUsers.fulfilled, (state, action) => {
        const response = action.payload;

        state.users = response.resultList;
        state.usersTotalMap = response.totalMap;
      })

      .addCase(getUserDetail.fulfilled, (state, action) => {
        state.userDetail = action.payload.resultObject;
      })

      // 작업자 안전 교육 이력 조회 성공
      .addCase(getWorkerSafetyHistories.fulfilled, (state, action) => {
        state.safetyHistories = action.payload.resultList;
      })

      .addCase(createUser.fulfilled, (state, action) => {
        state.dialog.type = IDialogType.alert;
        state.dialog.isNavigate = true;
        state.dialog.message = action.payload.message;
      })
      .addCase(createUser.rejected, (state, action) => {
        state.dialog.type = IDialogType.alert;
        state.dialog.message = action.payload.message;
      })

      .addCase(updateUser.fulfilled, (state, action) => {
        state.dialog.type = IDialogType.alert;
        state.dialog.message = action.payload.message;
      })
      .addCase(updateUser.rejected, (state, action) => {
        state.dialog.type = IDialogType.alert;
        state.dialog.message = action.payload.message;
      })

      .addCase(deleteUser.fulfilled, (state, action) => {
        state.dialog.type = IDialogType.alert;
        state.dialog.message = action.payload.message;
      })

      .addCase(deleteUserData.fulfilled, (state, action) => {
        state.dialog.type = IDialogType.alert;
        state.dialog.message = action.payload.message;
      })

      .addCase(getUserFileUrl.fulfilled, (state, action) => {
        state.userDetail = {
          ...state.userDetail,
          fileUrl: action.payload
        };
      });
  }
});

export const user = userSlice.name;
export const userReducer = userSlice.reducer;
export const userActions = userSlice.actions;
export const {
  changeFetchUsersRequest,
  changeUserDetail,
  changeFetchUserSafetyHistoriesRequest,
  resetFetchUserRequest,
  resetFetchUserSafetyHistoriesRequest,
  resetAll,
  changeDialog,
  resetDialog,
  changePageAuthority,
  resetUserDetail
} = userSlice.actions;
export default userSlice.reducer;
